/**
 * pageSubmissionUtils.ts
 *
 * Utilities for preparing page submission payloads and handling answer validation.
 *
 * Key Functions:
 * - preparePageAnswersPayload() - Build submission payload for custom endpoint
 * - ensureBooleanFieldDefaults() - Add missing boolean field defaults
 * - filterVisibleAnswers() - Filter answers based on field visibility
 */

import { evaluateFieldConditions } from "../../utils/fieldConditions";
import { FormVersionDetails, FormPage, FormSection } from "../../types/form-types";
import { Field } from "../../types/types";

// Runtime type for form version with converted fields
interface RuntimeFormVersionDetails extends Omit<FormVersionDetails, 'form_structure'> {
  form_structure: {
    pages: FormPage[];
    fields: Field[];
    sections?: FormSection[];
  };
}

/**
 * Answer value wrapper with metadata for submission
 */
export interface SubmissionAnswerValue {
  value: any;
  type: "user-entered" | "default";
  userId?: string;
  timestamp: string;
}

/**
 * Prepare page answers for submission to custom endpoint
 * Includes metadata and ensures all boolean fields have values
 * @param answersMap - Map of field answers
 * @param pageFieldIds - Set of field IDs on current page
 * @param allFields - All form fields (for validation)
 * @param currentValuesRecord - Answers as record (for condition evaluation)
 * @param userId - User ID for metadata
 * @returns Record of page answers with metadata
 */
export const preparePageAnswersPayload = (
  answersMap: Map<string, any>,
  pageFieldIds: Set<string>,
  allFields: Field[],
  currentValuesRecord: Record<string, any>,
  userId?: string
): Record<string, SubmissionAnswerValue> => {
  const answersRecord: Record<string, SubmissionAnswerValue> = {};

  // Add answered fields from current page
  answersMap.forEach((value, fieldId) => {
    if (pageFieldIds.has(fieldId)) {
      // Check visibility one last time before submitting
      const field = allFields.find(f => f.id === fieldId);
      const isVisible = field && (
        field.type === 'hidden' || // Always include true hidden fields in payload
        !field.conditions ||
        field.conditions.length === 0 ||
        evaluateFieldConditions(field, currentValuesRecord, allFields)
      );

      if (isVisible) {
        // Format in inspection-style with metadata
        answersRecord[fieldId] = {
          value: value,
          type: "user-entered",
          userId: userId,
          timestamp: new Date().toISOString()
        };
      }
    }
  });

  return answersRecord;
};

/**
 * Ensure all boolean/radio/yes-no-toggle fields on a page have default values
 * Adds false as default for unanswered boolean fields
 * @param pageFields - Fields on current page
 * @param answersRecord - Current answer payload
 * @param currentValuesRecord - Answers as record (for condition evaluation)
 * @param allFields - All form fields (for condition evaluation)
 * @param userId - User ID for metadata
 * @returns Updated answers record with defaults added
 */
export const ensureBooleanFieldDefaults = (
  pageFields: Field[],
  answersRecord: Record<string, SubmissionAnswerValue>,
  currentValuesRecord: Record<string, any>,
  allFields: Field[],
  userId?: string
): Record<string, SubmissionAnswerValue> => {
  const updated = { ...answersRecord };

  pageFields.forEach((field: Field) => {
    // Check if this is a boolean/yes-no-toggle field that hasn't been answered yet
    // Note: multiple-choice fields with boolean options are also treated as boolean fields
    const isBooleanField = (field.type === "multiple-choice" && (
      field.options?.some((opt: any) => opt.value === "true" || opt.value === "false")
    )) || field.type === "yes-no-toggle";

    if (isBooleanField && !updated.hasOwnProperty(field.id)) {
      // Check visibility before adding
      const isVisible = !field.conditions ||
        field.conditions.length === 0 ||
        evaluateFieldConditions(field, currentValuesRecord, allFields);

      if (isVisible) {
        // Default boolean/radio/yes-no-toggle fields to false if not answered
        updated[field.id] = {
          value: false,
          type: "default",
          userId: userId,
          timestamp: new Date().toISOString()
        };
      }
    }
  });

  return updated;
};

/**
 * Filter answers to only include visible fields from a page
 * @param answers - Map of all answers
 * @param pageFieldIds - Set of field IDs on page
 * @param allFields - All form fields
 * @param currentValuesRecord - Answers as record (for condition evaluation)
 * @returns Filtered record of only visible page answers
 */
export const filterVisibleAnswers = (
  answers: Map<string, any>,
  pageFieldIds: Set<string>,
  allFields: Field[],
  currentValuesRecord: Record<string, any>
): Record<string, any> => {
  const filtered: Record<string, any> = {};

  answers.forEach((value, fieldId) => {
    if (!pageFieldIds.has(fieldId)) return;

    // Check visibility one last time before including
    const field = allFields.find(f => f.id === fieldId);
    const isVisible = field && (
      field.type === 'hidden' || // Always include true hidden fields
      !field.conditions ||
      field.conditions.length === 0 ||
      evaluateFieldConditions(field, currentValuesRecord, allFields)
    );

    if (isVisible) {
      filtered[fieldId] = value;
    }
  });

  return filtered;
};

/**
 * Get all page field IDs for a specific page
 * @param formVersion - Form version with structure (accepts both API and runtime types)
 * @param pageId - Page ID to get fields for
 * @returns Set of field IDs on page
 */
export const getPageFieldIds = (
  formVersion: FormVersionDetails | RuntimeFormVersionDetails | null,
  pageId?: string
): Set<string> => {
  if (!formVersion?.form_structure?.fields || !pageId) {
    return new Set();
  }

  const fields = formVersion.form_structure.fields as Field[];
  return new Set(
    fields
      .filter((f) => f.pageId === pageId)
      .map((f) => f.id)
  );
};

/**
 * Find phone field in form for storing in sessionStorage
 * Looks for common phone field patterns
 * @param allFields - All form fields
 * @returns Phone field or undefined
 */
export const findPhoneField = (allFields: Field[]): Field | undefined => {
  return allFields.find((f: Field) => {
    const dataKey = f.dataKey?.toLowerCase() || '';
    const label = f.label?.toLowerCase() || '';
    return (
      dataKey.includes('phone') ||
      dataKey.includes('mobile') ||
      dataKey.includes('cell') ||
      label.includes('phone') ||
      label.includes('mobile') ||
      label.includes('cell')
    );
  });
};

/**
 * Build submission metadata from answers
 * @param answersRecord - Prepared answers record with metadata
 * @returns Count of answers by type
 */
export const getSubmissionMetadata = (
  answersRecord: Record<string, SubmissionAnswerValue>
): { userEntered: number; defaults: number; total: number } => {
  const userEntered = Object.values(answersRecord).filter(a => a.type === 'user-entered').length;
  const defaults = Object.values(answersRecord).filter(a => a.type === 'default').length;

  return {
    userEntered,
    defaults,
    total: userEntered + defaults
  };
};
